import { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface CountdownTimerProps {
  targetDate: string;
  onComplete?: () => void;
  compact?: boolean;
  className?: string;
}

/**
 * Countdown timer component that displays time remaining until a target date
 * Updates every second and calls onComplete when countdown reaches zero
 */
export function CountdownTimer({
  targetDate,
  onComplete,
  compact = false,
  className = ''
}: CountdownTimerProps) {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const calculateTimeRemaining = (): TimeRemaining | null => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        if (!isComplete) {
          setIsComplete(true);
          onComplete?.();
        }
        return null;
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      };
    };

    // Initial calculation
    setTimeRemaining(calculateTimeRemaining());

    // Update every second
    const interval = setInterval(() => {
      const newTime = calculateTimeRemaining();
      setTimeRemaining(newTime);
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate, onComplete, isComplete]);

  if (!timeRemaining || isComplete) return null;

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 font-mono ${className}`}>
        <span className="font-semibold">
          {timeRemaining.days}d {timeRemaining.hours}h {timeRemaining.minutes}m {timeRemaining.seconds}s
        </span>
      </div>
    );
  }

  return (
    <div className={`countdown-timer ${className}`}>
      <div className="countdown-display flex justify-center gap-4 md:gap-8">
        <TimeUnit value={timeRemaining.days} label="Days" />
        <TimeUnit value={timeRemaining.hours} label="Hours" />
        <TimeUnit value={timeRemaining.minutes} label="Minutes" />
        <TimeUnit value={timeRemaining.seconds} label="Seconds" />
      </div>
    </div>
  );
}

const TimeUnit = memo(function TimeUnit({ value, label }: { value: number; label: string }) {
  return (
    <div className="time-unit flex flex-col items-center">
      <div className="time-value-wrapper relative overflow-hidden">
        <AnimatePresence mode="popLayout">
          <motion.div
            key={value}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: 'easeOut',
            }}
            className="text-4xl md:text-6xl font-bold font-viking text-orange-500 bg-turquoise-900 rounded-lg px-4 py-2 md:px-6 md:py-3 shadow-lg min-w-[80px] md:min-w-[120px] text-center"
          >
            {value.toString().padStart(2, '0')}
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="time-label text-sm md:text-base text-turquoise-700 font-semibold mt-2 uppercase tracking-wide">
        {label}
      </div>
    </div>
  );
});
